// iter116 Session 2 — Modal "Prise en charge" : choix Mobile / Sur place + QR + Open on this device
import { useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PriseEnChargeModal({ rdvId, open, onClose, onSurPlace }) {
  const [phase, setPhase] = useState("choice"); // "choice" | "qr"
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleMobile = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/rdv/${rdvId}/prise-en-charge/init`,
        { mode: "mobile" },
        { headers: authHeaders() }
      );
      // Si l'URL retournée par le backend est relative, on la complète avec window.location.origin
      const url = data.url?.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
      setQrData({ ...data, url });
      setPhase("qr");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération QR");
    } finally {
      setLoading(false);
    }
  };

  const handleSurPlace = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/rdv/${rdvId}/prise-en-charge/init`,
        { mode: "sur_place" },
        { headers: authHeaders() }
      );
      const url = data.url?.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
      onSurPlace && onSurPlace({ ...data, url });
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const reset = () => { setPhase("choice"); setQrData(null); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="pec-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        {phase === "choice" && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Prise en charge du véhicule</h2>
            <p className="text-sm text-gray-600 mb-4">Comment souhaites-tu faire la prise en charge ?</p>
            <div className="space-y-3">
              <button
                onClick={handleMobile}
                disabled={loading}
                className="w-full p-4 rounded-xl border-2 border-[#52a675] bg-[#52a675]/5 hover:bg-[#52a675]/10 text-left active:scale-[0.98]"
                data-testid="pec-choice-mobile"
              >
                <div className="font-semibold text-[#2d5f3f] mb-0.5">📱 Mobile (avec QR code)</div>
                <div className="text-xs text-gray-600">Le receveur scanne le QR et remplit le formulaire sur son téléphone</div>
              </button>
              <button
                onClick={handleSurPlace}
                disabled={loading}
                className="w-full p-4 rounded-xl border-2 border-gray-300 hover:border-gray-400 bg-white text-left active:scale-[0.98]"
                data-testid="pec-choice-surplace"
              >
                <div className="font-semibold text-gray-800 mb-0.5">💻 Sur place (poste)</div>
                <div className="text-xs text-gray-600">Saisie directement depuis cet ordinateur</div>
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded" data-testid="pec-close">Annuler</button>
            </div>
          </>
        )}

        {phase === "qr" && qrData && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-1">📱 Prise en charge mobile</h2>
            <p className="text-sm text-gray-600 mb-3">Scanne le QR ci-dessous depuis le téléphone du receveur ou ouvre directement sur cet appareil.</p>
            <div className="bg-white border-2 border-gray-100 rounded-xl p-4 flex items-center justify-center" data-testid="pec-qr-container">
              <QRCodeCanvas value={qrData.url} size={220} level="M" includeMargin={false} />
            </div>
            <div className="mt-3">
              <a
                href={qrData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-3 rounded-xl bg-[#52a675] text-white font-semibold hover:bg-[#3d8a5c] active:scale-[0.98]"
                data-testid="pec-open-here"
              >
                🚀 Ouvrir sur cet appareil
              </a>
              <div className="text-[11px] text-gray-500 mt-1.5 text-center break-all" data-testid="pec-url">{qrData.url}</div>
              <div className="text-[10px] text-gray-400 mt-0.5 text-center">Lien valable jusqu'à {new Date(qrData.expires_at).toLocaleString("fr-CH")}</div>
            </div>
            <div className="flex justify-between mt-4">
              <button onClick={reset} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">← Retour</button>
              <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded" data-testid="pec-done">Fermer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
